/* Web Push (PWA) subscription helper.
 *
 * Bridges the browser PushManager to the backend so the admin gets REAL OS
 * notifications — even when the app is fully closed. This is what makes the
 * mobile/iOS/Android ping work; the in-page Notification path in
 * notificationService.js only fires while a tab is open in the background.
 *
 * Flow:
 *   1. ensurePushSubscription() — called when the user enables/unmutes the bell,
 *      and on app load if notifications are already granted. It registers (or
 *      reuses) the service worker, subscribes via PushManager using the VAPID
 *      public key fetched from the backend, and POSTs the subscription so the
 *      server can push to it.
 *   2. disablePushSubscription() — called on mute; unsubscribes locally and
 *      tells the backend to forget this device.
 *
 * All calls are best-effort and swallow errors (returns false) so a push hiccup
 * never blocks the UI.
 */
import axios from './axios';

const PUSH_FLAG = 'push_subscribed';

const isPushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

// VAPID keys travel as URL-safe base64; PushManager wants a Uint8Array.
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) arr[i] = raw.charCodeAt(i);
  return arr;
};

// The SW is registered by serviceWorkerRegistration in production. In case it
// isn't ready yet (or we're testing), fall back to registering it here.
const getRegistration = async () => {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const existing = await navigator.serviceWorker.getRegistration();
    if (existing) return navigator.serviceWorker.ready;
  } catch (e) { /* ignore */ }
  try {
    await navigator.serviceWorker.register('/service-worker.js');
    return navigator.serviceWorker.ready;
  } catch (e) {
    return null;
  }
};

/**
 * Subscribe this device to web push and register it with the backend.
 * @returns {Promise<boolean>} true if the device is now subscribed.
 */
export const ensurePushSubscription = async () => {
  if (!isPushSupported()) return false;
  if (Notification.permission !== 'granted') return false;

  try {
    const { data } = await axios.get('/admin/push/config');
    if (!data?.enabled || !data?.publicKey) return false;

    const reg = await getRegistration();
    if (!reg) return false;

    // Reuse an existing subscription if its key still matches the server's;
    // otherwise drop it and make a fresh one (keys may have rotated).
    let sub = await reg.pushManager.getSubscription();
    if (sub) {
      const current = new Uint8Array(sub.options?.applicationServerKey || []);
      const wanted = urlBase64ToUint8Array(data.publicKey);
      const matches = current.length === wanted.length && current.every((b, i) => b === wanted[i]);
      if (!matches) {
        try { await sub.unsubscribe(); } catch (e) { /* ignore */ }
        sub = null;
      }
    }
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      });
    }

    // Background op fired on app load (when notifications are granted) — must NOT
    // pop a global "Created successfully" toast every time the panel opens.
    await axios.post('/admin/push/subscribe', { subscription: sub.toJSON() }, { skipNotify: true });
    try { localStorage.setItem(PUSH_FLAG, '1'); } catch (e) { /* ignore */ }
    return true;
  } catch (e) {
    return false;
  }
};

/** Unsubscribe this device and tell the backend to forget it. */
export const disablePushSubscription = async () => {
  if (!isPushSupported()) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg && (await reg.pushManager.getSubscription());
    if (sub) {
      try { await axios.post('/admin/push/unsubscribe', { endpoint: sub.endpoint }, { skipNotify: true }); } catch (e) { /* ignore */ }
      try { await sub.unsubscribe(); } catch (e) { /* ignore */ }
    }
  } catch (e) { /* ignore */ }
  try { localStorage.removeItem(PUSH_FLAG); } catch (e) { /* ignore */ }
};

export { isPushSupported };
